// Host-side renderer for untrusted custom widgets.
//
// SECURITY MODEL (see docs/security.md):
//  - The iframe loads widget-sandbox.html from a SEPARATE origin (VITE_SANDBOX_ORIGIN).
//  - sandbox="allow-scripts" only → the frame gets a NULL origin and cannot read
//    cookies, localStorage, or reach the parent DOM. We never add allow-same-origin.
//  - allow attribute denies powerful features (geolocation, camera, mic, …).
//  - A per-frame random nonce binds the postMessage channel; we validate both the
//    message origin and event.source on every inbound message.
import { useEffect, useRef, useState } from "react";
import type { WidgetSource } from "@/core/types";

const SANDBOX_ORIGIN = import.meta.env.VITE_SANDBOX_ORIGIN ?? window.location.origin;

interface WidgetFrameProps {
  source: WidgetSource;
  config: Record<string, unknown>;
  data?: Record<string, unknown>;
}

function randomNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function WidgetFrame({ source, config, data }: WidgetFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(120);
  const nonceRef = useRef<string>(randomNonce());

  useEffect(() => {
    const nonce = nonceRef.current;
    const sandboxOrigin = new URL(SANDBOX_ORIGIN).origin;

    function onMessage(event: MessageEvent) {
      // Only trust the sandbox origin + our frame + our nonce.
      if (event.origin !== sandboxOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      const msg = event.data as { nonce?: string; type?: string; payload?: unknown };
      if (msg.nonce !== nonce) return;

      if (msg.type === "READY") {
        iframeRef.current?.contentWindow?.postMessage(
          { nonce, type: "INIT", payload: { source, config, data } },
          sandboxOrigin,
        );
      } else if (msg.type === "RESIZE") {
        const h = (msg.payload as { height?: number })?.height;
        if (typeof h === "number") setHeight(Math.min(Math.max(h, 40), 2000));
      }
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [source, config, data]);

  const src = `${SANDBOX_ORIGIN}/widget-sandbox.html?host=${encodeURIComponent(
    window.location.origin,
  )}&nonce=${nonceRef.current}`;

  return (
    <iframe
      ref={iframeRef}
      title="custom-widget"
      src={src}
      // allow-scripts ONLY. Never add allow-same-origin here.
      sandbox="allow-scripts"
      allow="geolocation 'none'; camera 'none'; microphone 'none'; payment 'none'"
      style={{ width: "100%", height, border: "none", background: "transparent" }}
    />
  );
}
